import {
  MAPTAC_WARNING_THRESHOLDS,
  RELIABILITY_FLAG_COLUMNS,
  computeDrinkingPredictionReliability
} from './drinkingPredictionReliability.js'

export const DEFAULT_PREDICTION_THRESHOLD = 0.50

const SCORE_META_TO_MAPTAC = {
  device_worn_hours_min: 'deviceWornHoursMin',
  very_negative_tac_hours_min: 'veryNegativeTacHoursMin',
  artifact_hours_min: 'artifactHoursMin',
  proba_unreliable_low: 'probaUnreliableLow',
  proba_unreliable_high: 'probaUnreliableHigh'
}

export function buildDrinkingPredictionReview(predictionThreshold, maptacThresholds) {
  return {
    drinking_prediction_review: {
      prediction_threshold: Number(predictionThreshold),
      maptac_thresholds: { ...maptacThresholds }
    }
  }
}

/**
 * Map processing ``drinking_day_score_meta`` into UI maptac threshold shape.
 * Prefers UI-aligned ``device_worn_hours_min``. Legacy ``non_wear_hours_min``
 * was non-wear hours (e.g. 6) incorrectly mapped 1:1 onto worn-min; convert
 * via 24h day so non_wear=6 → worn=18.
 */
export function maptacFromScoreMeta(scoreMeta = {}) {
  const out = { ...MAPTAC_WARNING_THRESHOLDS }

  for (const [snake, camel] of Object.entries(SCORE_META_TO_MAPTAC)) {
    if (scoreMeta[snake] !== undefined && scoreMeta[snake] !== null) {
      out[camel] = Number(scoreMeta[snake])
    }
  }

  if (
    (scoreMeta.device_worn_hours_min === undefined || scoreMeta.device_worn_hours_min === null)
    && scoreMeta.non_wear_hours_min !== undefined
    && scoreMeta.non_wear_hours_min !== null
  ) {
    out.deviceWornHoursMin = Math.max(0, 24 - Number(scoreMeta.non_wear_hours_min))
  }

  return out
}

export function hydrateDrinkingPredictionReview(processingResults = {}) {
  const review = processingResults.drinking_prediction_review
  const scoreMeta = processingResults.drinking_day_score_meta || {}

  let predictionThreshold = DEFAULT_PREDICTION_THRESHOLD
  let maptacThresholds = maptacFromScoreMeta(scoreMeta)

  if (review && typeof review === 'object') {
    if (review.prediction_threshold !== undefined && review.prediction_threshold !== null) {
      predictionThreshold = Number(review.prediction_threshold)
    }
    if (review.maptac_thresholds && typeof review.maptac_thresholds === 'object') {
      maptacThresholds = { ...maptacThresholds, ...review.maptac_thresholds }
    }
  }

  return { predictionThreshold, maptacThresholds }
}

export function computePredFromProba(day, predictionThreshold) {
  const proba = Number(day?.proba_drinking_day)
  if (day?.proba_drinking_day === undefined || day?.proba_drinking_day === null || Number.isNaN(proba)) {
    return day?.pred_drinking_day !== undefined && day?.pred_drinking_day !== null
      ? Number(day.pred_drinking_day)
      : null
  }
  return proba >= Number(predictionThreshold) ? 1 : 0
}

export function buildDayPredictionRevision(day, predictionThreshold, maptacThresholds, { recomputePred = true } = {}) {
  const dayWithPred = recomputePred
    ? { ...day, pred_drinking_day: computePredFromProba(day, predictionThreshold) }
    : day
  const flags = computeDrinkingPredictionReliability(dayWithPred, maptacThresholds)
  const columns = { pred_drinking_day: dayWithPred.pred_drinking_day }
  for (const col of RELIABILITY_FLAG_COLUMNS) {
    columns[col] = flags[col]
  }
  return columns
}

export function diffDayRevision(day, newColumns) {
  const changed = {}
  for (const [col, val] of Object.entries(newColumns)) {
    const oldVal = day?.[col] !== undefined && day?.[col] !== null ? Number(day[col]) : null
    const newVal = val !== undefined && val !== null ? Number(val) : val
    if (oldVal !== newVal) {
      changed[col] = newVal
    }
  }
  return changed
}

export function buildDayRevisionPayloads(days, predictionThreshold, maptacThresholds, { recomputePred = true } = {}) {
  const revisions = []
  const updatesByDate = new Map()

  for (const day of days) {
    const newColumns = buildDayPredictionRevision(day, predictionThreshold, maptacThresholds, { recomputePred })
    const changedCols = diffDayRevision(day, newColumns)
    if (!Object.keys(changedCols).length) continue
    revisions.push({ row_match: { date: day.date }, columns: changedCols })
    updatesByDate.set(day.date, { ...day, ...newColumns })
  }

  return { revisions, updatesByDate }
}
